import { TrackingProperties as TrackingPropertiesType } from 'common-types';
import { useMemo } from 'react';

import { ProductDetails } from './common-event-properties';
import { Tracking } from './tracking';
import { Events } from './tracking-events/event';
import { TrackingTool } from './tracking-tool';

export type TrackingProperties = TrackingPropertiesType;

export type CommonTrackingProperties = Partial<{
  bigIntId?: string;
  productName: string;
  channel: string;
  pageURL: string;
  ip?: string;
  pageTitle: string;
  ssoUserId?: string;
}>;

type UseTracking<
  TCategory extends string,
  TAction extends string,
  TLabel extends string,
> = {
  trackEvent: (options: {
    category: TCategory;
    action: TAction;
    label: TLabel;
    customData?: Partial<TCustomData>;
  }) => void;
  trackCommonEvent: (event: Events, properties?: TrackingProperties) => void;
  startTracking: (trackingToolNames?: string[]) => void;
  stopTracking: (trackingToolNames?: string[]) => void;
  specialEvents: SpecialEvents;
};

export function useTracking<
  TCategory extends string,
  TAction extends string,
  TLabel extends string,
>(
  trackingTools: TrackingTool[],
  productDetails: ProductDetails,
): UseTracking<TCategory, TAction, TLabel> {
  const tracking = useMemo(
    () => new Tracking(trackingTools, productDetails),
    [],
  );
  return {
    trackEvent: tracking.trackEvent,
    startTracking: tracking.startTracking,
    stopTracking: tracking.stopTracking,
    specialEvents: tracking.specialEvents,
    trackCommonEvent: tracking.trackCommonEvent,
  };
}

export type SpecialEvents = {
  signIn: (userDetails: TUserDetails) => void;
  onUserCreated: () => void;
  pageView: (pageView: Partial<TPageView>) => void;
};

type TUserDetails = {
  name: string;
  ssoId: number;
  email: string;
};

type TPageView = {
  productName: string;
  journalName: string;
  publisherName: string;
  flowType: string;
  anonymousOrSignedIn: string;
  pageURL: string;
  sourceURL: string;
  ip: string;
  pageTitle: string;
  utm_source: string | string[] | undefined;
  utm_medium: string | string[] | undefined;
  utm_campaign: string | string[] | undefined;
};

type TCustomData = { [key: string]: string | number };
